import type { ZodType, ZodTypeDef } from 'zod';
import { ApiRequestError, NetworkError, toApiError } from './errors.js';

/**
 * The one way this app talks to the API.
 *
 * Auth is cookie-based: a short-lived access cookie and an httpOnly refresh cookie, both set
 * by the server. The browser will only send them cross-origin when every request carries
 * `credentials: 'include'`, which is why no feature module is allowed to call `fetch`
 * directly — forgetting it produces an anonymous request that fails in a confusing way.
 *
 * When the access cookie has expired the server answers 401. This client then calls the
 * refresh endpoint exactly ONCE, and if that succeeds replays the original request. Concurrent
 * 401s share a single refresh (a dashboard firing six queries must not rotate the refresh token
 * six times — with server-side reuse detection that would log the shop out). If the refresh
 * fails, the 401 is surfaced and `onSessionExpired` fires so the shell can send the user to
 * the login screen.
 *
 * Assumed contract (task 3 builds it):
 *   POST {baseUrl}/auth/refresh   -> 200 and rotated cookies, or 401 when the session is over.
 */

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type QueryValue = string | number | boolean | null | undefined | readonly string[];
export type QueryParams = Record<string, QueryValue>;

export interface RequestConfig<TResult> {
  method: HttpMethod;
  path: string;
  body?: unknown;
  query?: QueryParams;
  /**
   * Validates the response against the shared contract. Strongly preferred over a bare cast.
   *
   * The third type argument is `unknown` on purpose, and it is not decoration. `ZodType<T>`
   * defaults its INPUT parameter to `T` as well, so a schema whose parsed output differs from
   * what it accepts — any field carrying a `.default()`, which is how a response contract adds
   * a field without breaking older recorded responses — would make TypeScript unify `TResult`
   * with the input side and hand every caller the pre-parse type. What this actually does is
   * turn an `unknown` payload into `TResult`, so that is what the signature should say.
   */
  schema?: ZodType<TResult, ZodTypeDef, unknown>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Do not attempt the transparent refresh-and-retry. Set on the auth endpoints themselves,
   * so a failed login never triggers a refresh loop.
   */
  skipAuthRefresh?: boolean;
  /**
   * `blob` hands back the body untouched, for the few endpoints that answer a file (a PDF to
   * show in a frame) rather than JSON. Errors are still read as the JSON envelope.
   */
  responseType?: 'json' | 'blob';
}

export type RequestOptions<TResult> = Omit<RequestConfig<TResult>, 'method' | 'path' | 'body'>;

export interface ApiClient {
  readonly baseUrl: string;
  request<TResult = unknown>(config: RequestConfig<TResult>): Promise<TResult>;
  get<TResult = unknown>(path: string, options?: RequestOptions<TResult>): Promise<TResult>;
  post<TResult = unknown>(
    path: string,
    body?: unknown,
    options?: RequestOptions<TResult>,
  ): Promise<TResult>;
  patch<TResult = unknown>(
    path: string,
    body?: unknown,
    options?: RequestOptions<TResult>,
  ): Promise<TResult>;
  put<TResult = unknown>(
    path: string,
    body?: unknown,
    options?: RequestOptions<TResult>,
  ): Promise<TResult>;
  del<TResult = unknown>(
    path: string,
    body?: unknown,
    options?: RequestOptions<TResult>,
  ): Promise<TResult>;
  /**
   * A file, as a `Blob`, through the same credentialed, refresh-on-401 path as everything else
   * — which is why a screen that wants to show a PDF fetches it here rather than pointing an
   * iframe at the API and hoping the cookie survives the trip.
   */
  getBlob(path: string, options?: Omit<RequestOptions<Blob>, 'schema'>): Promise<Blob>;
  /** Test seam: how many times the refresh endpoint has been called. */
  readonly refreshCount: () => number;
}

export interface ApiClientOptions {
  /** Absolute origin + version prefix, e.g. `http://localhost:4000/api/v1`. */
  baseUrl: string;
  /** Injected in tests; defaults to the global `fetch`. */
  fetch?: typeof globalThis.fetch;
  /** Path of the refresh endpoint, relative to `baseUrl`. */
  refreshPath?: string;
  /** Called once when a 401 could not be recovered by refreshing. */
  onSessionExpired?: () => void;
  /**
   * Shown when the server returns something that is not the shared error envelope — an HTML
   * error page from a proxy, say. Required, and deliberately so: it is a user-visible string,
   * and this layer must not be the one place in the app that hardcodes English copy.
   */
  unexpectedErrorMessage: string;
}

const JSON_MIME = 'application/json';

export function buildUrl(baseUrl: string, path: string, query?: QueryParams): string {
  const base = baseUrl.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, item);
    } else {
      search.append(key, String(value));
    }
  }

  const queryString = search.toString();
  return queryString ? `${base}${suffix}?${queryString}` : `${base}${suffix}`;
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;
  const text = await response.text();
  if (text.length === 0) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const {
    baseUrl,
    fetch: fetchImpl,
    refreshPath = '/auth/refresh',
    onSessionExpired,
    unexpectedErrorMessage,
  } = options;

  const doFetch: typeof globalThis.fetch =
    fetchImpl ?? ((input, init) => globalThis.fetch(input, init));

  /** Shared across concurrent 401s so the refresh token rotates once, not once per request. */
  let refreshInFlight: Promise<boolean> | null = null;
  let refreshCount = 0;

  async function performRefresh(): Promise<boolean> {
    refreshCount += 1;
    try {
      const response = await doFetch(buildUrl(baseUrl, refreshPath), {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: JSON_MIME },
      });
      return response.ok;
    } catch {
      // The network died mid-refresh. Not a dead session — just a failed attempt.
      return false;
    }
  }

  function refreshOnce(): Promise<boolean> {
    refreshInFlight ??= performRefresh().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  }

  async function send<TResult>(config: RequestConfig<TResult>): Promise<Response> {
    const hasBody = config.body !== undefined && config.method !== 'GET';
    const init: RequestInit = {
      method: config.method,
      // Every request is credentialed: the API lives on another origin and auth is cookies.
      credentials: 'include',
      headers: {
        Accept: JSON_MIME,
        ...(hasBody ? { 'Content-Type': JSON_MIME } : {}),
        ...config.headers,
      },
      ...(hasBody ? { body: JSON.stringify(config.body) } : {}),
      ...(config.signal ? { signal: config.signal } : {}),
    };

    try {
      return await doFetch(buildUrl(baseUrl, config.path, config.query), init);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new NetworkError(undefined, cause);
    }
  }

  async function toResult<TResult>(
    response: Response,
    config: RequestConfig<TResult>,
  ): Promise<TResult> {
    if (response.ok && config.responseType === 'blob') return (await response.blob()) as TResult;

    const payload = await readBody(response);

    if (!response.ok) {
      throw new ApiRequestError(
        response.status,
        toApiError(response.status, payload, unexpectedErrorMessage),
      );
    }

    if (!config.schema) return payload as TResult;

    const parsed = config.schema.safeParse(payload);
    if (!parsed.success) {
      // The server broke the shared contract. Loud, and never silently rendered as empty.
      throw new ApiRequestError(response.status, {
        code: 'invalid_response',
        message: unexpectedErrorMessage,
        details: { issues: parsed.error.issues },
      });
    }
    return parsed.data;
  }

  async function request<TResult = unknown>(config: RequestConfig<TResult>): Promise<TResult> {
    const response = await send(config);

    if (response.status === 401 && !config.skipAuthRefresh) {
      const refreshed = await refreshOnce();
      if (refreshed) {
        // Exactly one replay. A second 401 after a successful refresh is a real 401.
        const retried = await send({ ...config, skipAuthRefresh: true });
        return await toResult(retried, config);
      }
      onSessionExpired?.();
    }

    return await toResult(response, config);
  }

  function get<TResult = unknown>(path: string, opts?: RequestOptions<TResult>): Promise<TResult> {
    return request<TResult>({ ...opts, method: 'GET', path });
  }

  function post<TResult = unknown>(
    path: string,
    body?: unknown,
    opts?: RequestOptions<TResult>,
  ): Promise<TResult> {
    return request<TResult>({ ...opts, method: 'POST', path, body });
  }

  function patch<TResult = unknown>(
    path: string,
    body?: unknown,
    opts?: RequestOptions<TResult>,
  ): Promise<TResult> {
    return request<TResult>({ ...opts, method: 'PATCH', path, body });
  }

  function put<TResult = unknown>(
    path: string,
    body?: unknown,
    opts?: RequestOptions<TResult>,
  ): Promise<TResult> {
    return request<TResult>({ ...opts, method: 'PUT', path, body });
  }

  function del<TResult = unknown>(
    path: string,
    body?: unknown,
    opts?: RequestOptions<TResult>,
  ): Promise<TResult> {
    return request<TResult>({ ...opts, method: 'DELETE', path, body });
  }

  function getBlob(path: string, opts?: Omit<RequestOptions<Blob>, 'schema'>): Promise<Blob> {
    return request<Blob>({ ...opts, method: 'GET', path, responseType: 'blob' });
  }

  return {
    baseUrl,
    request,
    refreshCount: () => refreshCount,
    get,
    getBlob,
    post,
    patch,
    put,
    del,
  };
}

/**
 * Where the API lives. Defined in `./base-url.ts` so a standalone public page can read it
 * without importing this module, and re-exported here so every existing caller is unaffected.
 */
export { API_BASE_URL, API_HEALTH_URL, API_ORIGIN } from './base-url.js';
